import {
  leadsContactsListOptions,
  leadsInvoiceContactsRetrieveOptions,
  LeadsListData,
  leadsListInfiniteOptions,
  PaginatedReturnLeadListReadable,
  ReturnLeadReadable
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import {
  AddContactModal,
  AddContractorModal,
  TagInput
} from '@crm/features/billing/components'
import { userTransformResults } from '@crm/utils'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Select
} from 'antd'
import { Add } from 'iconsax-react'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

type Props = {
  form: FormInstance
  invoiceId?: number
  contractorId?: number
  setContractorId?: Dispatch<SetStateAction<number | undefined>>
}

export const ContactsInvoiceForm = ({
  form,
  invoiceId,
  contractorId,
  setContractorId
}: Props) => {
  const { t } = useLocales()

  const [isOpenContractorModal, setIsOpenContractorModal] = useState(false)
  const [isOpenContactModal, setIsOpenContactModal] = useState(false)

  const { data: leadContacts } = useQuery({
    ...leadsContactsListOptions({ path: { lead_id: String(contractorId) } }),
    enabled: !!contractorId
  })

  const { data } = useQuery({
    ...leadsInvoiceContactsRetrieveOptions({
      path: { id: Number(contractorId) }
    }),
    enabled: !!contractorId
  })

  useEffect(() => {
    if (data) {
      const fields: Partial<{
        contacts: number[]
        additional_emails: string[]
      }> = {}

      if (!invoiceId) {
        fields.contacts = data?.contacts?.map((contact) => contact.id) ?? []
      }

      fields.additional_emails = data?.additional_emails ?? []

      form.setFieldsValue(fields)
    }
  }, [data, form, invoiceId])

  return (
    <>
      <AddContractorModal
        open={isOpenContractorModal}
        onClose={() => setIsOpenContractorModal(false)}
        setContractorId={setContractorId}
      />

      <AddContactModal
        open={isOpenContactModal}
        leadId={contractorId}
        onClose={() => setIsOpenContactModal(false)}
      />

      <Card title={t('billing.contacts')} size="small">
        <Flex gap={8} vertical>
          <Row justify="space-between" gutter={12}>
            <Col span={12}>
              <Form.Item
                label={t('billing.choose_contractor')}
                name="lead"
                required
                rules={[
                  {
                    required: true,
                    message: t('billing.contractor_required')
                  }
                ]}
              >
                <InfiniteSelect<
                  LeadsListData,
                  PaginatedReturnLeadListReadable,
                  ReturnLeadReadable
                >
                  popupMatchSelectWidth
                  placeholder={t('billing.choose_contractor')}
                  getQueryFunction={leadsListInfiniteOptions}
                  getOptions={({ options }) => userTransformResults(options)}
                  disabled={!!invoiceId}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('billing.contacts')}
                name="contacts"
                required
                rules={[
                  {
                    required: true,
                    message: t('billing.contacts_required')
                  }
                ]}
              >
                <Select
                  disabled={!contractorId}
                  options={
                    leadContacts && userTransformResults(leadContacts?.results)
                  }
                  mode="multiple"
                  placeholder={t('billing.choose_contacts')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              {!invoiceId ? (
                <Button
                  icon={<Add />}
                  type="text"
                  onClick={() => setIsOpenContractorModal(true)}
                >
                  {t('billing.new_contractor')}
                </Button>
              ) : (
                <Form.Item label={t('billing.invoice')} name="invoice_number">
                  <Input placeholder={t('billing.invoice')} />
                </Form.Item>
              )}
            </Col>

            <Col span={12}>
              <Button
                icon={<Add />}
                type="text"
                onClick={() => setIsOpenContactModal(true)}
                disabled={!contractorId}
              >
                {t('buttons.add_contact')}
              </Button>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item
                label={t('billing.additional_emails')}
                name="additional_emails"
              >
                <TagInput placeholder={t('billing.additional_emails')} />
              </Form.Item>
            </Col>
          </Row>
        </Flex>
      </Card>
    </>
  )
}
